import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import api, { apiErr, fmtDate, money } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, Th, Td, AdminHeading, adminInput } from "@/components/admin/ui";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

function NewReservation({ onDone }) {
  const [open, setOpen] = useState(false);
  const [exps, setExps] = useState([]);
  const [f, setF] = useState({ experience_id: "", arrival_date: "", departure_date: "", adults: 2, children: 0, contact_email: "", status: "Approved", override_conflicts: false, override_reason: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { if (open) api.get("/admin/experiences").then((r) => setExps(r.data)).catch(() => {}); }, [open]);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.post("/admin/reservations", { ...f, adults: +f.adults, children: +f.children }); toast.success(`Created ${data.confirmation_number}`); setOpen(false); onDone(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.conflicts && !f.override_conflicts) { toast.error("Date conflict — enable override with a reason to proceed."); set("override_conflicts", true); }
      else toast.error(apiErr(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-gold px-5 py-2.5 text-sm flex items-center gap-2" data-testid="admin-new-reservation"><Plus size={15} /> New Reservation</button></DialogTrigger>
      <DialogContent className="bg-surface border-white/10 max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New Reservation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select className={adminInput} value={f.experience_id} onChange={(e) => set("experience_id", e.target.value)} data-testid="admin-res-exp">
            <option value="">Select experience…</option>{exps.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input type="date" className={adminInput} value={f.arrival_date} onChange={(e) => set("arrival_date", e.target.value)} />
            <input type="date" className={adminInput} value={f.departure_date} onChange={(e) => set("departure_date", e.target.value)} />
            <input type="number" min="1" className={adminInput} value={f.adults} onChange={(e) => set("adults", e.target.value)} placeholder="Adults" />
            <input type="number" min="0" className={adminInput} value={f.children} onChange={(e) => set("children", e.target.value)} placeholder="Children" />
          </div>
          <input type="email" className={adminInput} value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="Guest email" data-testid="admin-res-email" />
          {f.override_conflicts && (
            <input className={adminInput} value={f.override_reason} onChange={(e) => set("override_reason", e.target.value)} placeholder="Reason for conflict override (required, audited)" />
          )}
          <button onClick={save} disabled={saving || !f.experience_id || !f.arrival_date || !f.departure_date} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid="admin-res-save">
            {saving ? <Loader2 className="animate-spin" size={15} /> : null} Create Reservation
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminReservations() {
  const [items, setItems] = useState(null);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const status = params.get("status") || "";
  const filter = params.get("filter") || "";
  const [filterLabel, setFilterLabel] = useState("");
  const [invalidFilter, setInvalidFilter] = useState(false);

  const load = () => {
    const p = {};
    if (status) p.status = status;
    if (filter) p.filter = filter;
    setInvalidFilter(false);
    api.get("/admin/reservations", { params: p })
      .then((r) => setItems(r.data))
      .catch((e) => {
        if (filter && e?.response?.status === 400) { setInvalidFilter(true); setItems([]); }
        else setItems([]);
      });
  };
  useEffect(() => { load(); }, [status, filter]);
  useEffect(() => {
    if (!filter) { setFilterLabel(""); return; }
    api.get("/admin/reservations-filter-label", { params: { filter } })
      .then((r) => setFilterLabel(r.data.label)).catch(() => setFilterLabel(""));
  }, [filter]);

  const clearFilter = () => setParams({});

  return (
    <div>
      <AdminHeading title={filterLabel || "Reservations"} subtitle={filter ? `${items?.length ?? 0} matching · filtered` : `${items?.length ?? 0} total`}>
        <NewReservation onDone={load} />
      </AdminHeading>
      {filter && (
        <div className="flex items-center gap-3 mb-4" data-testid="active-filter">
          <span className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-gold/15 border border-gold/40 text-gold">
            Filter: {filterLabel || filter}
          </span>
          <button onClick={clearFilter} className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="clear-filter">Clear filter</button>
          <button onClick={() => nav("/admin")} className="text-xs text-muted-foreground hover:text-foreground underline">← Back to dashboard</button>
        </div>
      )}
      {invalidFilter && (
        <p className="text-sm text-red-300 mb-4" data-testid="invalid-filter">Unknown filter "{filter}". <button onClick={clearFilter} className="underline">Show all reservations</button></p>
      )}
      <div className="flex gap-2 mb-4 flex-wrap">
        {["", "Request Submitted", "Approved", "Confirmed", "In House", "Checked Out", "Cancelled"].map((s) => (
          <button key={s} onClick={() => setParams(s ? { status: s } : {})}
            className={`px-3 py-1.5 rounded-full text-xs border ${status === s && !filter ? "bg-pool/15 border-pool/40 text-pool" : "border-white/10 text-muted-foreground"}`}>
            {s || "All"}
          </button>
        ))}
      </div>
      <Panel>
        {items === null ? <Loading /> : items.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No reservations.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead><tr><Th>Conf.</Th><Th>Guest</Th><Th>Experience</Th><Th>Dates</Th><Th>Status</Th><Th>Payment</Th><Th className="text-right">Balance</Th></tr></thead>
              <tbody>{items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-white/5" onClick={() => nav(`/admin/reservations/${r.id}`)} data-testid={`admin-res-row-${r.id}`}>
                  <Td className="text-gold whitespace-nowrap">{r.confirmation_number}</Td>
                  <Td className="whitespace-nowrap">{r.contact_email}</Td>
                  <Td>{r.experience_title}</Td>
                  <Td className="whitespace-nowrap text-muted-foreground">{fmtDate(r.arrival_date)} → {fmtDate(r.departure_date)}</Td>
                  <Td><StatusBadge status={r.status} /></Td>
                  <Td className="text-xs text-muted-foreground">{r.payment_status}</Td>
                  <Td className="text-right">{money(r.balance)}</Td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
